import { useTranslation } from "next-i18next";
import {
  getBankByBankCode,
  thDistrict,
  thProvince,
  thSubDistrict,
} from "src/utils/master-data";
import { useMemo, useState } from "react";
import { useRouter } from "next/router";
import { maskBankAccountNumber, maskCitizenId } from "src/utils/maskData";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import ConfirmDialog from "src/components/Common/ConfirmDialog/ConfirmDialog";
import {
  BannerImage,
  Button,
  ButtonTitle,
  Content,
  Modal,
  ModalBody,
  PopupDivider,
  Title,
} from "./PayoutModal.style";
import InfoSection from "./InfoSection/InfoSection";

export type BankAccountInfoType = {
  bankCode: string;
  accountName: string;
  accountNumber: string;
};

export type PersonalInfoType = {
  identityNumber: string;
};

export type AddressInfoType = {
  address: string;
  villageBldg?: string;
  moo?: string;
  soi?: string;
  road?: string;
  zipCode: string;
  province: string;
  district: string;
  subDistrict: string;
};

export type PayoutModalProps = {
  bankAccountInfo?: BankAccountInfoType;
  personalInfo?: PersonalInfoType;
  addressInfo?: AddressInfoType;
  isBankAccountValid?: boolean;
  isPersonalValid?: boolean;
  isAddressValid?: boolean;
  visible?: boolean;
  setVisible?: (value: boolean) => void;
};

const PayoutModal = ({
  bankAccountInfo,
  personalInfo,
  addressInfo,
  isBankAccountValid,
  isPersonalValid,
  isAddressValid,
  visible,
  setVisible,
}: PayoutModalProps) => {
  const { t } = useTranslation("affiliate");
  const router = useRouter();
  const { locale } = router;
  const { xs } = useBreakpoint();
  const authUrl = process.env.NEXT_PUBLIC_AUTH_URL;
  const [confirmDialogVisible, setConfirmDialogVisible] =
    useState<boolean>(false);
  const [editUrl, setEditUrl] = useState<string>("");

  const bankAccountSectionData = useMemo(() => {
    const { bankCode, accountName, accountNumber } = bankAccountInfo || {};
    const region = process.env.NEXT_PUBLIC_REGION || "";
    const bankList = getBankByBankCode(region.toLocaleUpperCase());
    const bankInfo = bankList?.[bankCode];
    const bankName = bankInfo?.name?.localize?.[locale];
    const maskedAccountNumber = accountNumber
      ? maskBankAccountNumber(accountNumber)
      : undefined;

    return [
      { key: "bank-name", i18nKey: "bankName", value: bankName },
      { key: "account-name", i18nKey: "accountName", value: accountName },
      {
        key: "account-number",
        i18nKey: "accountNumber",
        value: maskedAccountNumber,
      },
    ];
  }, [bankAccountInfo, locale]);

  const personalInfoSectionData = useMemo(() => {
    const { identityNumber } = personalInfo || {};
    const maskedIdentityNumber = identityNumber
      ? maskCitizenId(identityNumber)
      : undefined;

    return [
      {
        key: "identity-number",
        i18nKey: "identityNumber",
        value: maskedIdentityNumber,
      },
    ];
  }, [personalInfo]);

  const addressInfoSectionData = useMemo(() => {
    const {
      address: addrNo,
      villageBldg,
      subDistrict: subDistrictCode,
      district: districtCode,
      province: provinceCode,
      zipCode,
    } = addressInfo || {};
    const addressNo = `${addrNo || ""} ${villageBldg || ""}`.trim();
    const subDistrict =
      thSubDistrict[subDistrictCode]?.name?.localize?.[locale];
    const district = thDistrict[districtCode]?.name?.localize?.[locale];
    const province = thProvince[provinceCode]?.name?.localize?.[locale];

    return [
      { key: "address-number", i18nKey: "addressNumber", value: addressNo },
      { key: "sub-district", i18nKey: "subDistrict", value: subDistrict },
      { key: "district", i18nKey: "district", value: district },
      { key: "province", i18nKey: "province", value: province },
      { key: "zipcode", i18nKey: "zipcode", value: zipCode },
    ];
  }, [addressInfo, locale]);

  const isAllSectionValid = useMemo(() => {
    return isBankAccountValid && isPersonalValid && isAddressValid;
  }, [isAddressValid, isBankAccountValid, isPersonalValid]);

  const handleOpenConfirmDialog = (url: string) => {
    setEditUrl(url);
    setConfirmDialogVisible(true);
  };

  const handleConfirmEdit = () => {
    window.open(editUrl);
    setConfirmDialogVisible(false);
  };

  const handleRejectConfirmEdit = () => {
    setEditUrl("");
    setConfirmDialogVisible(false);
  };

  const handleOpenBankAccountEdit = () => {
    const url = `${authUrl}/${locale}/setting/payment#payout`;
    if (isAllSectionValid) {
      handleOpenConfirmDialog(url);
    } else {
      window.open(url);
    }
  };

  const handleOpenPersonalEdit = () => {
    const url = `${authUrl}/${locale}/setting/profile-info`;
    if (isAllSectionValid) {
      handleOpenConfirmDialog(url);
    } else {
      window.open(url);
    }
  };

  const handleOpenAddressEdit = () => {
    const url = `${authUrl}/${locale}/setting/profile-info`;
    if (isAllSectionValid) {
      handleOpenConfirmDialog(url);
    } else {
      window.open(url);
    }
  };

  const handleCloseModal = () => {
    setVisible(false);
  };

  return (
    <>
      <ConfirmDialog
        title={t("payout.editNotice.title")}
        description={t("payout.editNotice.description")}
        confirmText={
          <ButtonTitle level={7}>
            {t("payout.editNotice.editInfoButton")}
          </ButtonTitle>
        }
        rejectText={
          <ButtonTitle level={7}>
            {t("payout.editNotice.closeButton")}
          </ButtonTitle>
        }
        onConfirm={handleConfirmEdit}
        onReject={handleRejectConfirmEdit}
        isVisible={confirmDialogVisible}
        fullWidthButton
        centered
      />
      <Modal
        data-testid="payout-affiliate-modal"
        title={
          <Title level={5} data-testid="payout-affiliate-modal-title">
            {t("payout.title")}
          </Title>
        }
        footer={
          <Button
            data-testid="payout-affiliate-close-modal-button"
            data-gtm-input-code="payout-affiliate-close-modal-button"
            fullWidth
            onClick={handleCloseModal}
          >
            <ButtonTitle level={7}>{t("payout.closeButton")}</ButtonTitle>
          </Button>
        }
        visible={visible}
        width={!xs ? 443 : "100vw"}
        onCancel={() => setVisible(false)}
        maskClosable={false}
        centered={!xs}
      >
        <ModalBody>
          <BannerImage
            alt="payout-affiliate"
            src="/static/pages/affiliatePage/payout-affiliate-banner.png"
          />
          <Content>
            <InfoSection
              sectionKey="payout-bank-account-info-section"
              sectionI18nKey="bankAccountInfoSection"
              isValid={isBankAccountValid}
              fields={bankAccountSectionData}
              onEdit={handleOpenBankAccountEdit}
            />
            <PopupDivider />
            <InfoSection
              sectionKey="payout-personal-info-section"
              sectionI18nKey="personalInfoSection"
              isValid={isPersonalValid}
              fields={personalInfoSectionData}
              onEdit={handleOpenPersonalEdit}
            />
            <PopupDivider />
            <InfoSection
              sectionKey="payout-address-info-section"
              sectionI18nKey="addressInfoSection"
              isValid={isAddressValid}
              fields={addressInfoSectionData}
              onEdit={handleOpenAddressEdit}
            />
          </Content>
        </ModalBody>
      </Modal>
    </>
  );
};

export default PayoutModal;
